// Locale-aware date formatting on top of the platform's Intl API.
//
// Intl is used rather than a date library because it localises numerals as well
// as month names — Arabic renders ١ يناير ٢٠٢٢, which date-fns does not do for
// any of its ar* locales — and because it adds nothing to the bundle and needs
// no locale registration. That last point matters: moment kept locales in
// global mutable state, which is what made `.locale('ar')` fail silently.
//
// Locale tags are explicit rather than bare language codes. The numbering
// system has to be stated for Arabic (`-u-nu-arab`): ICU resolves plain 'ar' to
// Western digits in Node, so leaving it implicit makes Jest and the browser
// disagree.
//
// Keys are the language codes stored on the user, in the canonical BCP-47 casing
// the language picker offers; values are the tag handed to Intl. Region matters
// even where the language is the same — the Englishes disagree on the numeric
// date outright (11/08 in the UK, 2022-08-11 in Canada), and so do fr-FR and
// fr-CA.
const DATE_LOCALE_TAGS: Record<string, string> = {
  ar: 'ar-u-nu-arab', // Arabic — numbering system pinned, see above
  de: 'de-DE', // German
  en: 'en', // English, region unspecified — the product's historical 'en'
  'en-CA': 'en-CA', // English (Canada)
  'en-GB': 'en-GB', // English (UK)
  'en-US': 'en-US', // English (US)
  es: 'es-ES', // Spanish, region unspecified — the product's historical 'es'
  fr: 'fr-FR', // French
  'fr-CA': 'fr-CA', // French (Canada)
  nl: 'nl-NL', // Dutch
  pt: 'pt-PT', // Portuguese, region unspecified
  'zh-CN': 'zh-CN', // Chinese (Simplified)
}

// Lookup index for the table above. BCP-47 is case-insensitive and the language
// arrives as an unchecked string off the user record, so matching happens against
// lowercased keys — without this, every region-qualified key ('en-GB', 'fr-CA')
// is unreachable and silently resolves to its base language instead.
const TAGS_BY_NORMALISED_KEY: Record<string, string> = Object.fromEntries(
  Object.entries(DATE_LOCALE_TAGS).map(([key, tag]) => [
    key.toLowerCase(),
    tag,
  ]),
)

// Region-less 'en' pins mm/dd/yyyy for the numeric date. Field order is a
// property of the locale and cannot be set through the options object, so the
// only way to ask for it is to name a locale that already orders it that way.
// Bare 'en' happens to be month-first in ICU today, which makes this belt and
// braces rather than a correction — it states the requirement instead of relying
// on an unqualified tag keeping its current default.
//
// Regional English is deliberately left out: someone who picked English (UK)
// means 11/08/2022 and English (Canada) means 2022-08-11. Every other language
// keeps its own convention too — forcing month-first on German would only make
// 11.08.2022 wrong.
const NUMERIC_DATE_LOCALE_TAGS: Record<string, string> = {
  en: 'en-US',
}

// Unknown and missing languages resolve here, which makes region-less English
// the fallback for both the tag and the numeric override above.
const DEFAULT_LANGUAGE = 'en'

// Named presets instead of token strings. Intl reorders fields per locale,
// which a literal pattern like 'MMM dd, yyyy' cannot do — that pattern imposes
// English word order on every language.
// `medium` spells out its components rather than using dateStyle: 'medium'.
// dateStyle collapses to an all-numeric date in some locales (ar → ٠١/٠١/٢٠٢٢,
// de → 01.01.2022), losing the month name that every call site expects. The
// explicit form reproduces moment's 'll' output. It is not a guarantee: ICU
// resolves the day+month+year skeleton to an all-numeric pattern for pt-PT, so
// Portuguese reads 1/01/2022 where every other language names the month.
//
// These options select *which* fields appear and how wide they are, never their
// order: Intl ignores the order the properties are written in and takes it from
// the locale instead. `default` is month-first for region-less English because
// toLocaleTag resolves it against en-US, not because `month` is listed before
// `day` here — the same preset is day-first in de, es, fr, nl, pt, ar and en-GB,
// and year-first in en-CA, fr-CA and zh-CN.
const DATE_FORMATS = {
  default: {month: '2-digit', day: '2-digit', year: 'numeric'}, // en: 08/11/2022
  medium: {day: 'numeric', month: 'short', year: 'numeric'}, // 1 Jan 2022
  monthYear: {month: 'long', year: 'numeric'}, // January 2022
  full: {dateStyle: 'full'}, // Saturday, 1 January 2022
  fullWithTime: {dateStyle: 'full', timeStyle: 'short'},
  mediumWithTime: {dateStyle: 'medium', timeStyle: 'medium'}, // en: Sep 29, 2026, 5:43:19 PM
  // en: Sep 23, 2026, 12:11:49 PM — a fixed, always-absolute timestamp for
  // threads (comments, activity logs) where relative time would keep
  // re-rendering and a bare date would drop the time of day.
  mediumWithSeconds: {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  },
  time: {hour: 'numeric', minute: '2-digit'}, // en: 5:43 PM
} satisfies Record<string, Intl.DateTimeFormatOptions>

export type TDateFormat = keyof typeof DATE_FORMATS

// Intl.DateTimeFormat construction is costly enough to matter in tables that
// format a cell per row, so formatters are memoised per tag + preset.
const formatterCache = new Map<string, Intl.DateTimeFormat>()

// Resolves whatever the user record carries to a normalised key. Underscores and
// case are normalised because the language is a string from the API, not a
// checked union — 'pt_BR' is a shape old QuestionPro payloads use. An unlisted
// region falls back to its base language ('de-AT' → 'de') rather than all the way
// to English, which would drop the month names too.
const toLanguageKey = (language?: string): string => {
  const normalised = language?.replace(/_/g, '-').toLowerCase()
  if (!normalised) {
    return DEFAULT_LANGUAGE
  }
  if (TAGS_BY_NORMALISED_KEY[normalised]) {
    return normalised
  }

  const baseLanguage = normalised.split('-')[0]
  return TAGS_BY_NORMALISED_KEY[baseLanguage] ? baseLanguage : DEFAULT_LANGUAGE
}

// Region-less English resolves to two tags depending on the preset — en-US for
// the all-numeric date, bare 'en' everywhere the month is spelled out. Every
// other key resolves to one tag for every preset.
const toLocaleTag = (
  language: string | undefined,
  dateFormat: TDateFormat,
): string => {
  const key = toLanguageKey(language)

  return (
    (dateFormat === 'default' ? NUMERIC_DATE_LOCALE_TAGS[key] : undefined) ??
    TAGS_BY_NORMALISED_KEY[key]
  )
}

const getFormatter = (
  language: string | undefined,
  dateFormat: TDateFormat,
  timeZone?: string,
): Intl.DateTimeFormat => {
  const tag = toLocaleTag(language, dateFormat)
  const cacheKey = `${tag}|${dateFormat}|${timeZone ?? 'local'}`
  const cached = formatterCache.get(cacheKey)
  if (cached) {
    return cached
  }

  const formatter = new Intl.DateTimeFormat(tag, {
    ...DATE_FORMATS[dateFormat],
    ...(timeZone ? {timeZone} : {}),
  })
  formatterCache.set(cacheKey, formatter)
  return formatter
}

// Returns '' for missing or unparseable input rather than the literal
// 'Invalid date' string moment produced.
//
// `timeZone` defaults to the viewer's zone, matching plain `moment(d).format()`.
// Pass 'UTC' where the calendar day itself is the data — a completion date
// compared against a due date, for instance, must be read on the same calendar
// that differenceInUtcDays uses, or the displayed date and the day count can
// disagree for viewers whose offset pushes them across midnight.
const formatDate = (
  date: Date | string | number | null | undefined,
  dateFormat: TDateFormat = 'medium',
  language?: string,
  timeZone?: string,
): string => {
  if (date === null || date === undefined || date === '') {
    return ''
  }

  const parsed = date instanceof Date ? date : new Date(date)
  if (Number.isNaN(parsed.getTime())) {
    return ''
  }

  return getFormatter(language, dateFormat, timeZone).format(parsed)
}

const MS_PER_DAY = 86_400_000

const toUtcDayStart = (date: Date): number =>
  Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())

// Whole-day difference measured on the UTC calendar — the replacement for
// moment's `.utc().startOf('day').diff(other, 'days')`.
//
// The UTC part is the point: truncating in local time would move a timestamp
// late in the day into the next or previous day depending on the viewer's
// offset, so two users could see a task as on-time and a day late. date-fns'
// differenceInCalendarDays truncates in local time, and forcing UTC through
// date-fns-tz's toZonedTime is easy to get subtly wrong — both operands here are
// UTC midnights, so the gap is an exact multiple of a day and plain arithmetic
// is exact with no DST correction needed.
//
// Returns NaN for unparseable input, matching what moment's diff produced.
const differenceInUtcDays = (
  laterDate: Date | string | number,
  earlierDate: Date | string | number,
): number => {
  const later = laterDate instanceof Date ? laterDate : new Date(laterDate)
  const earlier =
    earlierDate instanceof Date ? earlierDate : new Date(earlierDate)

  if (Number.isNaN(later.getTime()) || Number.isNaN(earlier.getTime())) {
    return NaN
  }

  return (toUtcDayStart(later) - toUtcDayStart(earlier)) / MS_PER_DAY
}

// Re-anchors a calendar day to UTC midnight, for fields that carry a date the
// user picked rather than a moment something happened (a due date, not a
// creation timestamp).
//
// WuDatePicker wraps react-day-picker, which emits days as *local* midnight
// (`new Date(y, m, d, 0, 0, 0, 0)`). Serialised to the API from UTC+6 that turns
// "3 January" into 2024-01-02T18:00:00Z, and the day a viewer sees then depends
// on their own offset. Reading the local calendar fields and re-stamping them as
// UTC makes the value mean the same day everywhere.
//
// The local fields are deliberate: `getFullYear` not `getUTCFullYear`. The input
// is a local-midnight Date, so its local fields are the day the user clicked.
const toUtcCalendarDay = (date: Date | string | number): Date => {
  const parsed = date instanceof Date ? date : new Date(date)
  if (Number.isNaN(parsed.getTime())) {
    return parsed
  }

  return new Date(
    Date.UTC(parsed.getFullYear(), parsed.getMonth(), parsed.getDate()),
  )
}

// toLocaleTag stays internal — DATE_LOCALE_TAGS is the seam an enabled-locale
// API would filter, and exporting a reader for it before anything consumes it
// only makes the module look like it has requirements it doesn't.
export const dateUtil = {
  formatDate,
  differenceInUtcDays,
  toUtcCalendarDay,
} as const

// Exported
